import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PublicApi } from '../constants';

export interface BrandDocument {
  colors: {
    primary: string;
    secondary?: string;
    accent?: string;
    on_primary?: string;
    heading?: string;
    text?: string;
    text_muted?: string;
    bg?: string;
    surface?: string;
    surface_alt?: string;
    sheet?: string;
    gradient_start?: string;
    gradient_end?: string;
    card?: string;
    card_border?: string;
    card_alt?: string;
    task?: string;
    success?: string;
    warning?: string;
    error?: string;
    completed?: string;
    disabled?: string;
    grey_1?: string;
    grey_2?: string;
    grey_3?: string;
    grey_4?: string;
    grey_5?: string;
    grey_6?: string;
  };
  colors_dark?: Record<string, string>;
  typography?: {
    source: 'system' | 'google' | 'upload';
    family?: string | null;
    family_ar?: string | null;
    heading_family?: string | null;
    google_family?: string | null;
    google_family_ar?: string | null;
    fallback?: string;
    base_size?: number;
  };
  shape?: {
    radius?: number;
    radius_lg?: number;
    button_radius?: number;
    shell_max?: number;
  };
  identity?: {
    display_name?: string;
    display_name_ar?: string;
    tagline?: string;
    tagline_ar?: string;
  };
  meta?: {
    title?: string;
    title_ar?: string;
    description?: string;
    description_ar?: string;
  };
  support?: {
    email?: string;
    phone?: string;
    whatsapp?: string;
    website?: string;
  };
  social?: {
    instagram?: string;
    x?: string;
    tiktok?: string;
    snapchat?: string;
    youtube?: string;
    linkedin?: string;
  };
  legal?: {
    privacy_url?: string;
    terms_url?: string;
  };
  defaults?: {
    language?: string;
    theme?: string;
  };
  flags?: {
    powered_by?: boolean;
  };
}

export interface CreateBrandPayload {
  name: string;
  description?: string;
  brand: BrandDocument;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  fonts?: any[];
  is_default?: boolean;
}

export interface UpdateBrandPayload {
  id: number;
  name?: string;
  description?: string;
  brand?: BrandDocument;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  fonts?: any[];
  is_active?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  private http = inject(HttpClient);

  getBrands(search?: string, includeInactive = true): Observable<any> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (includeInactive !== undefined) {
      params = params.set('include_inactive', includeInactive.toString());
    }
    return this.http.get<any>(PublicApi.BRANDING_LIST, { params });
  }

  createBrand(payload: CreateBrandPayload): Observable<any> {
    return this.http.post<any>(PublicApi.BRANDING_CREATE, payload);
  }

  updateBrand(payload: UpdateBrandPayload): Observable<any> {
    return this.http.post<any>(PublicApi.BRANDING_UPDATE, payload);
  }

  setDefaultBrand(id: number): Observable<any> {
    return this.http.post<any>(PublicApi.BRANDING_SET_DEFAULT, { id });
  }

  // Domain CRUD
  getDomains(): Observable<any> {
    return this.http.get<any>(PublicApi.DOMAIN_LIST);
  }

  // zero_storage is a create-time decision, so the raw key crosses Luqta exactly
  // once — here. The response carries the mode and a non-secret hint; the key
  // itself is never returned by any endpoint.
  createDomain(payload: {
    hostname: string;
    branding_id: number;
    is_primary?: boolean;
    notes?: string;
    zero_storage?: boolean;
    zero_storage_key?: string;
  }): Observable<any> {
    return this.http.post<any>(PublicApi.DOMAIN_CREATE, payload);
  }

  /**
   * domain/update refuses zero_storage_key outright, and refuses zero_storage
   * whenever it differs from the stored value. Both are dropped here rather than
   * at the call site, so a caller that posts a whole domain row back cannot make
   * an ordinary edit — a re-point, a notes change, a primary toggle — fail on a
   * field it never meant to send.
   */
  updateDomain(payload: { id: number; branding_id?: number; is_primary?: boolean; is_enabled?: boolean; notes?: string }): Observable<any> {
    const { zero_storage, zero_storage_key, ...body } = payload as Record<string, any>;
    return this.http.post<any>(PublicApi.DOMAIN_UPDATE, body);
  }

  verifyDomain(id: number): Observable<any> {
    return this.http.post<any>(PublicApi.DOMAIN_VERIFY, { id });
  }

  deleteDomain(id: number): Observable<any> {
    return this.http.post<any>(PublicApi.DOMAIN_DELETE, { id });
  }
}
